const COMBINED_URL_REGEX = new RegExp(
  '(https?:\\/\\/[^\\s<]+)|' +
    '((?:www\\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(?:\\/[^\\s<]*)?)',
  'gi'
);

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const MEDIA_FILE_EXTENSION_REGEX =
  /\.(?:apng|avif|bmp|gif|heic|heif|ico|jpe?g|jfif|m4v|mkv|mov|mp4|mpeg|mpg|ogg|ogv|png|svg|tiff?|avi|webm|webp|wmv|3gp)$/i;

const MARKDOWN_LINK_REGEX =
  /\[([^\]\r\n]+)\]\(((?:mailto:|https?:\/\/|www\.)[^\s)]+)\)/gi;

type LinkCandidate = {
  href: string;
  index: number;
  lastIndex: number;
  text: string;
};

const isProtocolOrWwwUrl = (url: string) =>
  /^https?:\/\//i.test(url) || /^www\./i.test(url);

const isLikelyListIndexFalsePositive = (url: string) =>
  !isProtocolOrWwwUrl(url) && /^\d{1,2}\.[a-z]{4,}(?:\/|$)/i.test(url);

const rangesOverlap = (
  start: number,
  end: number,
  rangeStart: number,
  rangeEnd: number
) => start < rangeEnd && end > rangeStart;

const unwrapAnchor = (anchor: HTMLAnchorElement) => {
  const fragment = anchor.ownerDocument.createDocumentFragment();
  while (anchor.firstChild) {
    fragment.appendChild(anchor.firstChild);
  }
  anchor.replaceWith(fragment);
};

const createSafeLink = (doc: Document, url: string, displayText = url) => {
  const anchor = doc.createElement('a');
  anchor.href = normalizeLinkHref(url);
  anchor.textContent = displayText;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  return anchor;
};

export const normalizeLinkHref = (url: string) => {
  if (/^mailto:/i.test(url)) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const getTokenBoundaryTextBefore = (text: string, index: number) => {
  const before = text.slice(0, index);
  return before.match(/[^\s]+$/)?.[0] ?? '';
};

const getTokenBoundaryTextAfter = (text: string, index: number) => {
  const after = text.slice(index);
  return after.match(/^[^\s]+/)?.[0] ?? '';
};

export const isLikelyMediaLink = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed || /^mailto:/i.test(trimmed)) return false;

  try {
    const parsed = new URL(normalizeLinkHref(trimmed));
    return MEDIA_FILE_EXTENSION_REGEX.test(
      decodeURIComponent(parsed.pathname).toLowerCase()
    );
  } catch {
    const path = trimmed.split(/[?#]/, 1)[0].toLowerCase();
    return MEDIA_FILE_EXTENSION_REGEX.test(path);
  }
};

export const isSingleAutoLinkUrlText = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed || isLikelyListIndexFalsePositive(trimmed)) return false;

  const isSingleUrl =
    /^https?:\/\/[^\s<]+$/i.test(trimmed) ||
    /^(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s<]*)?$/i.test(
      trimmed
    );

  return isSingleUrl && !isLikelyMediaLink(trimmed);
};

export const getAutoLinkHrefFromText = (text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  EMAIL_REGEX.lastIndex = 0;
  const emailMatch = EMAIL_REGEX.exec(trimmed);
  if (
    emailMatch &&
    emailMatch.index === 0 &&
    emailMatch[0].length === trimmed.length
  ) {
    return `mailto:${emailMatch[0]}`;
  }

  return isSingleAutoLinkUrlText(trimmed) ? normalizeLinkHref(trimmed) : null;
};

const shouldFlattenMarkdownLinkToPlainText = (
  fullText: string,
  matchStart: number,
  matchEnd: number,
  label: string
) => {
  const left = getTokenBoundaryTextBefore(fullText, matchStart);
  const right = getTokenBoundaryTextAfter(fullText, matchEnd);
  const combinedHref = getAutoLinkHrefFromText(`${left}${label}${right}`);
  if (combinedHref) return true;
  return Boolean(getAutoLinkHrefFromText(label));
};

const getTextNodesForLinkNormalization = (root: HTMLElement) => {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parentElement = node.parentElement ?? null;
        if (!parentElement) return NodeFilter.FILTER_REJECT;
        if (parentElement.closest('a, img, video, style, script')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );

  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  return textNodes;
};

const getLinkCandidates = (text: string): LinkCandidate[] => {
  const candidates: LinkCandidate[] = [];

  EMAIL_REGEX.lastIndex = 0;
  let emailMatch = EMAIL_REGEX.exec(text);
  while (emailMatch) {
    const email = emailMatch[0];
    candidates.push({
      href: `mailto:${email}`,
      index: emailMatch.index,
      lastIndex: emailMatch.index + email.length,
      text: email
    });
    emailMatch = EMAIL_REGEX.exec(text);
  }

  COMBINED_URL_REGEX.lastIndex = 0;
  let urlMatch = COMBINED_URL_REGEX.exec(text);
  while (urlMatch) {
    const url = urlMatch[0];
    const index = urlMatch.index;
    const lastIndex = index + url.length;
    const overlapsEmail = candidates.some(
      (candidate) =>
        candidate.href.startsWith('mailto:') &&
        rangesOverlap(index, lastIndex, candidate.index, candidate.lastIndex)
    );

    if (
      !overlapsEmail &&
      !isLikelyListIndexFalsePositive(url) &&
      !isLikelyMediaLink(url)
    ) {
      candidates.push({
        href: url,
        index,
        lastIndex,
        text: url
      });
    }

    urlMatch = COMBINED_URL_REGEX.exec(text);
  }

  return candidates.sort(
    (a, b) => a.index - b.index || b.lastIndex - a.lastIndex
  );
};

const textNeedsLinkNormalization = (text: string) => {
  if (!text.trim()) return false;
  MARKDOWN_LINK_REGEX.lastIndex = 0;
  if (MARKDOWN_LINK_REGEX.test(text)) return true;
  return getLinkCandidates(text).length > 0;
};

const normalizeMarkdownLinksInTextNode = (textNode: Text) => {
  const text = textNode.textContent ?? '';
  const doc = textNode.ownerDocument;
  MARKDOWN_LINK_REGEX.lastIndex = 0;
  const matches = Array.from(text.matchAll(MARKDOWN_LINK_REGEX));
  if (matches.length === 0) return false;

  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;

  matches.forEach((match) => {
    const fullMatch = match[0];
    const label = match[1];
    const href = match[2];
    const matchStart = match.index ?? 0;
    const matchEnd = matchStart + fullMatch.length;

    if (matchStart > lastIndex) {
      fragment.appendChild(
        doc.createTextNode(text.slice(lastIndex, matchStart))
      );
    }

    if (
      isLikelyMediaLink(href) ||
      shouldFlattenMarkdownLinkToPlainText(text, matchStart, matchEnd, label)
    ) {
      fragment.appendChild(doc.createTextNode(label));
    } else {
      fragment.appendChild(createSafeLink(doc, href, label));
    }

    lastIndex = matchEnd;
  });

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
  return true;
};

const linkifyTextNode = (textNode: Text) => {
  const text = textNode.textContent ?? '';
  const candidates = getLinkCandidates(text);
  const doc = textNode.ownerDocument;
  if (candidates.length === 0) return false;

  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;
  let matched = false;

  candidates.forEach((candidate) => {
    if (candidate.index < lastIndex) return;
    matched = true;
    if (candidate.index > lastIndex) {
      fragment.appendChild(
        doc.createTextNode(text.slice(lastIndex, candidate.index))
      );
    }
    fragment.appendChild(createSafeLink(doc, candidate.href, candidate.text));
    lastIndex = candidate.lastIndex;
  });

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
  return matched;
};

const isAnchorPartOfLargerAutoLink = (
  anchor: HTMLAnchorElement,
  autoLinkText: string,
  autoHref: string
) => {
  const left = getTokenBoundaryTextBefore(
    anchor.previousSibling?.textContent ?? '',
    (anchor.previousSibling?.textContent ?? '').length
  );
  const right = getTokenBoundaryTextAfter(
    anchor.nextSibling?.textContent ?? '',
    0
  );
  if (!left && !right) return false;

  const combinedHref = getAutoLinkHrefFromText(
    `${left}${autoLinkText}${right}`
  );
  return Boolean(combinedHref && combinedHref !== autoHref);
};

const getAnchorAutoLinkState = (anchor: HTMLAnchorElement) => {
  if (anchor.querySelector('img, video')) return null;

  const text = (anchor.textContent ?? '').trim();
  const autoHref = getAutoLinkHrefFromText(text);
  if (!autoHref) return null;

  return {
    text,
    autoHref
  };
};

const anchorNeedsLinkNormalization = (anchor: HTMLAnchorElement) => {
  const state = getAnchorAutoLinkState(anchor);
  if (!state) return false;

  const { text, autoHref } = state;
  if (isAnchorPartOfLargerAutoLink(anchor, text, autoHref)) return true;
  if (anchor.getAttribute('href') !== autoHref) return true;
  if (!/^mailto:/i.test(autoHref) && anchor.target !== '_blank') return true;
  if (!/^mailto:/i.test(autoHref) && anchor.rel !== 'noopener noreferrer') {
    return true;
  }

  return false;
};

const hasRichTextLinksToNormalize = (root: HTMLElement) => {
  const anchors = Array.from(root.querySelectorAll('a'));
  if (anchors.some(anchorNeedsLinkNormalization)) {
    return true;
  }

  return getTextNodesForLinkNormalization(root).some((node) =>
    textNeedsLinkNormalization(node.textContent ?? '')
  );
};

export const normalizeRichTextAnchorsInContainer = (root: HTMLElement) => {
  const anchors = Array.from(root.querySelectorAll('a'));
  let changed = false;
  anchors.forEach((anchor) => {
    const state = getAnchorAutoLinkState(anchor);
    if (!state) return;

    const { text, autoHref } = state;
    if (!anchorNeedsLinkNormalization(anchor)) return;

    if (isAnchorPartOfLargerAutoLink(anchor, text, autoHref)) {
      unwrapAnchor(anchor);
      changed = true;
      return;
    }

    if (anchor.getAttribute('href') !== autoHref) {
      anchor.setAttribute('href', autoHref);
      changed = true;
    }

    if (!/^mailto:/i.test(autoHref) && anchor.target !== '_blank') {
      anchor.target = '_blank';
      changed = true;
    }

    if (!/^mailto:/i.test(autoHref) && anchor.rel !== 'noopener noreferrer') {
      anchor.rel = 'noopener noreferrer';
      changed = true;
    }
  });

  return changed;
};

const normalizeMarkdownLinksInContainer = (root: HTMLElement) => {
  let changed = false;
  getTextNodesForLinkNormalization(root).forEach((node) => {
    if (normalizeMarkdownLinksInTextNode(node)) changed = true;
  });

  return changed;
};

const linkifyPlainTextNodesInContainer = (root: HTMLElement) => {
  let changed = false;
  getTextNodesForLinkNormalization(root).forEach((node) => {
    if (linkifyTextNode(node)) changed = true;
  });
  return changed;
};

export const normalizeRichTextLinksInContainer = (root: HTMLElement) => {
  if (!hasRichTextLinksToNormalize(root)) {
    return false;
  }

  const markdownChanged = normalizeMarkdownLinksInContainer(root);
  const anchorsChanged = normalizeRichTextAnchorsInContainer(root);
  let changed = markdownChanged || anchorsChanged;

  if (changed) {
    root.normalize();
  }

  const linkified = linkifyPlainTextNodesInContainer(root);
  if (linkified) {
    changed = true;
  }

  return changed;
};
